import { Box, Button, Input } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Field } from '@mangovoice/mango-ui'
import type { WebhookConfigDetail } from '@mangovoice/types'

import type { ApiError } from '../api/types'
import { brandedButtonCss, isValidHttpsUrl } from '../utils'

export interface DolphinConfigFormProps {
  integration: WebhookConfigDetail
  isPending: boolean
  isError: boolean
  error: ApiError | null
  onSave: (data: { remote_id: string }) => void
}

export const DolphinConfigForm = ({
  integration,
  isPending,
  isError,
  error,
  onSave,
}: DolphinConfigFormProps) => {
  const { t } = useTranslation()
  const [webhookUrlValue, setWebhookUrlValue] = useState<string | null>(null)
  const [webhookUrlError, setWebhookUrlError] = useState<string | null>(null)

  const currentWebhookUrl = webhookUrlValue ?? integration.remote_id ?? ''

  const handleSave = () => {
    const trimmed = currentWebhookUrl.trim()
    if (!trimmed) {
      setWebhookUrlError('This field is required.')
      return
    }
    if (!isValidHttpsUrl(trimmed)) {
      setWebhookUrlError('Must be a valid secure (https://) URL.')
      return
    }
    setWebhookUrlError(null)
    onSave({ remote_id: trimmed })
  }

  return (
    <Box
      maxW="560px"
      width="full"
      mx="auto"
      display="flex"
      flexDirection="column"
      gap={4}
    >
      <Field.Root required invalid={!!webhookUrlError}>
        <Field.Label>{t('webhooks.dolphin.webhookUrl')}</Field.Label>
        <Input
          value={currentWebhookUrl}
          onChange={(e) => {
            setWebhookUrlValue(e.target.value)
            setWebhookUrlError(null)
          }}
        />
        {webhookUrlError && (
          <Field.ErrorText>{webhookUrlError}</Field.ErrorText>
        )}
        {!webhookUrlError && isError && (
          <Field.ErrorText>
            {(error && 'message' in error ? error.message : null) ??
              t('webhooks.common.failedToSave')}
          </Field.ErrorText>
        )}
        {integration.documentation_url && (
          <Box textAlign="right" mt={1}>
            <a
              href={integration.documentation_url}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: 'var(--chakra-font-sizes-sm)',
                color: 'var(--chakra-colors-fg-muted)',
                textDecoration: 'underline',
              }}
            >
              {t('webhooks.common.learnMore')}
            </a>
          </Box>
        )}
      </Field.Root>

      <Box display="flex" justifyContent="center">
        <Button onClick={handleSave} loading={isPending} css={brandedButtonCss}>
          {!integration.remote_id
            ? t('webhooks.common.createIntegration')
            : t('webhooks.common.updateIntegration')}
        </Button>
      </Box>
    </Box>
  )
}
